import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import type { TemplateStructureResponseEntity } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { MESSAGE_TEMPLATE_QUERY_KEY } from "./message-template.query.key";

const STRUCTURE_CACHE_KEY = "template-structure-cache";
const STRUCTURE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

interface CachedStructure {
  data: TemplateStructureResponseEntity;
  timestamp: number;
}

/** Get cached structure from localStorage if valid (within TTL) */
const getCachedStructure = (): TemplateStructureResponseEntity | null => {
  try {
    const cached = localStorage.getItem(STRUCTURE_CACHE_KEY);
    if (!cached) {
      return null;
    }

    const parsed = JSON.parse(cached) as CachedStructure;
    const now = Date.now();

    // Check if cache is still valid
    if (now - parsed.timestamp < STRUCTURE_CACHE_TTL) {
      return parsed.data;
    }

    // Cache expired, remove it
    localStorage.removeItem(STRUCTURE_CACHE_KEY);
    return null;
  } catch {
    // If parsing fails, clear the cache
    localStorage.removeItem(STRUCTURE_CACHE_KEY);
    return null;
  }
};

/** Save structure to localStorage with timestamp */
const setCachedStructure = (data: TemplateStructureResponseEntity): void => {
  try {
    const cached: CachedStructure = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(STRUCTURE_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage might be full or disabled, ignore
  }
};

/** Clear the structure cache (call after mutations) */
export const clearStructureCache = (): void => {
  try {
    localStorage.removeItem(STRUCTURE_CACHE_KEY);
  } catch {
    // Ignore errors
  }
};

export const queryOptions_message_template_structure = () =>
  queryOptions({
    queryKey: [MESSAGE_TEMPLATE_QUERY_KEY, "structure"] as const,
    queryFn: async () => {
      // Check localStorage cache first
      const cached = getCachedStructure();
      if (cached) {
        return cached;
      }

      // Fetch from API
      const data = await extractionWrapper(api.messageTemplate.getStructure());

      // Cache the result
      if (data) {
        setCachedStructure(data);
      }

      return data;
    },
    // Use staleTime to match the localStorage TTL
    staleTime: STRUCTURE_CACHE_TTL,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

export const useMessageTemplateStructure = () => {
  return useQuery(queryOptions_message_template_structure());
};
